// ============================================================================
// 📦 VERSION: 2025.12.04.0002
// ============================================================================
// CHANGELOG:
// 2025.12.04.0001 - Initial creation - Rewards API helper
// 2025.12.04.0002 - Added reward claiming functions
// ============================================================================

import { getToken } from './api'; // Your existing API helper

const API_URL = process.env.REACT_APP_API_URL || 'https://api.traktag.com';

// ============================================================================
// REWARD LOGGING
// ============================================================================

/**
 * Log a reward to the database
 * @param {Object} reward - Reward details
 * @param {string} reward.reward_type - Type: 'achievement', 'grid_bonus', 'correct_answer', 'challenge_approved', 'referral', 'admin_award', 'new_player'
 * @param {string} reward.description - Human-readable description
 * @param {number} reward.traks_earned - TRAKs earned (default 0)
 * @param {number} reward.tags_earned - Tags earned (default 0)
 * @param {number} reward.game_id - Optional game ID
 * @param {number} reward.question_id - Optional question ID
 * @param {Object} reward.metadata - Optional extra data
 * @returns {Object} - { success, reward_id, claimed, requires_claim }
 */
export const logReward = async (reward) => {
  const token = getToken();
  if (!token) {
    console.log('⚠️ Cannot log reward - user not logged in');
    return null;
  }

  try {
    const response = await fetch(`${API_URL}/rewards`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(reward)
    });

    if (response.ok) {
      const result = await response.json();
      const claimStatus = result.requires_claim ? '🎁 (claim pending)' : '✅';
      console.log(`${claimStatus} Reward logged:`, reward.reward_type, '-', reward.description);
      return result;
    } else {
      console.error('❌ Failed to log reward:', await response.text());
      return null;
    }
  } catch (error) {
    console.error('❌ Error logging reward:', error);
    return null;
  }
};

// ============================================================================
// REWARD FETCHING
// ============================================================================

/**
 * Get player's reward history
 * @param {Object} options - Query options
 * @param {number} options.limit - Max results (default 50)
 * @param {number} options.offset - Pagination offset (default 0)
 * @param {string} options.type - Filter by reward type (optional)
 */
export const getRewards = async (options = {}) => {
  const token = getToken();
  if (!token) return null;

  const { limit = 50, offset = 0, type = null } = options;
  
  let url = `${API_URL}/rewards?limit=${limit}&offset=${offset}`;
  if (type) url += `&type=${type}`;

  try {
    const response = await fetch(url, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (response.ok) {
      return await response.json();
    }
    return null;
  } catch (error) {
    console.error('❌ Error fetching rewards:', error);
    return null;
  }
};

/**
 * Get rewards summary by type
 */
export const getRewardsSummary = async () => {
  const token = getToken();
  if (!token) return null;

  try {
    const response = await fetch(`${API_URL}/rewards/summary`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (response.ok) {
      return await response.json();
    }
    return null;
  } catch (error) {
    console.error('❌ Error fetching rewards summary:', error);
    return null;
  }
};

// ============================================================================
// REWARD CLAIMING
// ============================================================================

/**
 * Get all unclaimed rewards for the current player
 * @returns {Object} - { success, unclaimed: [], totals: { count, traks, tags } }
 */
export const getUnclaimedRewards = async () => {
  const token = getToken();
  if (!token) return null;

  try {
    const response = await fetch(`${API_URL}/rewards/unclaimed`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (response.ok) {
      return await response.json();
    }
    return null;
  } catch (error) {
    console.error('❌ Error fetching unclaimed rewards:', error);
    return null;
  }
};

/**
 * Claim a specific reward
 * @param {number} rewardId - The reward ID to claim
 * @returns {Object} - { success, claimed: { traks, tags }, newTotals: { total_traks, total_tags } }
 */
export const claimReward = async (rewardId) => {
  const token = getToken();
  if (!token) return null;

  try {
    const response = await fetch(`${API_URL}/rewards/claim/${rewardId}`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (response.ok) {
      const result = await response.json();
      console.log('🎉 Reward claimed!', result);
      return result;
    } else {
      const error = await response.json();
      console.error('❌ Failed to claim reward:', error.message);
      return { success: false, message: error.message };
    }
  } catch (error) {
    console.error('❌ Error claiming reward:', error);
    return null;
  }
};

/**
 * Claim all pending rewards at once
 * @returns {Object} - { success, claimed: { count, traks, tags }, newTotals: { total_traks, total_tags } }
 */
export const claimAllRewards = async () => {
  const token = getToken();
  if (!token) return null;

  try {
    const response = await fetch(`${API_URL}/rewards/claim-all`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (response.ok) {
      const result = await response.json();
      console.log('🎉 All rewards claimed!', result);
      return result;
    } else {
      const error = await response.json();
      console.error('❌ Failed to claim rewards:', error.message);
      return { success: false, message: error.message };
    }
  } catch (error) {
    console.error('❌ Error claiming rewards:', error);
    return null;
  }
};

// ============================================================================
// CONVENIENCE FUNCTIONS FOR COMMON REWARD TYPES
// ============================================================================

/**
 * Log achievement milestone reward (CLAIMABLE - player must claim)
 */
export const logAchievementReward = (milestoneName, traksRequired, bonusTraks) => {
  return logReward({
    reward_type: 'achievement',
    description: `${milestoneName} - You've hit ${traksRequired.toLocaleString()} TRAKs! +${bonusTraks} TRAK Bonus`,
    traks_earned: bonusTraks,
    metadata: { milestone_name: milestoneName, traks_required: traksRequired }
  });
};
/**
 * Log picture grid bonus reward (AUTO-CLAIMED)
 */
export const logGridBonusReward = (traksEarned, gameId, gameName, isSpecialBonus = false) => {
  return logReward({
    reward_type: 'grid_bonus',
    description: isSpecialBonus 
      ? `Special Bonus Cell in ${gameName}` 
      : `Picture Grid bonus in ${gameName}`,
    traks_earned: traksEarned,
    game_id: gameId,
    metadata: { is_special_bonus: isSpecialBonus }
  });
};

/**
 * Log correct answer reward (AUTO-CLAIMED)
 */
export const logCorrectAnswerReward = (gameId, gameName, questionId) => {
  return logReward({
    reward_type: 'correct_answer',
    description: `Correct answer in ${gameName}`,
    traks_earned: 1,
    tags_earned: 1,
    game_id: gameId,
    question_id: questionId
  });
};

/**
 * Log new player bonus reward (AUTO-CLAIMED)
 */
export const logNewPlayerReward = (traksEarned) => {
  return logReward({
    reward_type: 'new_player',
    description: 'Welcome bonus for new player',
    traks_earned: traksEarned,
    metadata: { bonus_type: 'first_play' }
  });
};

/**
 * Log challenge approved reward (CLAIMABLE - player must claim)
 */
export const logChallengeApprovedReward = (traksEarned, questionText) => {
  return logReward({
    reward_type: 'challenge_approved',
    description: `Challenge approved: "${questionText.substring(0, 30)}..."`,
    traks_earned: traksEarned,
    metadata: { question_preview: questionText.substring(0, 100) }
  });
};

/**
 * Log referral bonus reward (CLAIMABLE - player must claim)
 */
export const logReferralReward = (referredPlayerName, traksEarned) => {
  return logReward({
    reward_type: 'referral',
    description: `Referral bonus: ${referredPlayerName} joined!`,
    traks_earned: traksEarned,
    metadata: { referred_player: referredPlayerName }
  });
};

export default {
  // Logging
  logReward,
  logAchievementReward,
  logGridBonusReward,
  logCorrectAnswerReward,
  logNewPlayerReward,
  logChallengeApprovedReward,
  logReferralReward,
  // Fetching
  getRewards,
  getRewardsSummary,
  // Claiming
  getUnclaimedRewards,
  claimReward,
  claimAllRewards
};
